import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { isSuperAdmin } from '../lib/auth';

/**
 * SuperAdmin-only banner that warns when the external BillPortal MySQL (the
 * source of synced/demo bill PDFs) is unreachable — so admins catch an outage
 * before users report "PDF not available". Renders nothing when healthy or for
 * non-SuperAdmins. Polls every 60s.
 */
export default function ExternalMysqlBanner() {
  const [state, setState] = useState({ reachable: true, error: null });
  const sa = isSuperAdmin();

  useEffect(() => {
    if (!sa) return undefined;
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get('/payments/v2/external-mysql-health');
        if (alive) setState({ reachable: data?.reachable !== false, error: data?.error || null });
      } catch {
        // 403 / network — don't alarm on the probe itself; treat as unknown-ok.
        if (alive) setState({ reachable: true, error: null });
      }
    };
    check();
    const t = setInterval(check, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [sa]);

  if (!sa || state.reachable) return null;

  return (
    <div
      className="w-full bg-red-600 text-white px-4 md:px-6 py-2.5 flex items-center gap-3 text-sm"
      data-testid="external-mysql-down-banner"
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="font-semibold">External BillPortal data source (MySQL) is unreachable.</span>
      <span className="opacity-90 hidden sm:inline">
        Synced &amp; demo bill PDFs won&apos;t load until it&apos;s restored. Check the EXTERNAL_MYSQL_* secrets / connectivity.
      </span>
      {state.error && (
        <span className="opacity-75 truncate hidden md:inline" data-testid="external-mysql-down-error">
          ({state.error})
        </span>
      )}
    </div>
  );
}
